import { useState, useMemo } from 'react';
import {
  Brain, Video, Coffee, Cpu, Activity, Box, Users, Monitor,
  MapPin, Star, Clock, Search, Filter, ChevronRight, X, Check,
  Calendar, ChevronLeft, Zap, Award, Building2, Plus, Bookmark,
  ArrowRight, CheckCircle, AlertCircle, Wifi
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

const LAB_TYPES = [
  { id: 'all', label: 'All Labs' },
  { id: 'Research', label: 'Research', icon: Brain, color: 'text-chart-1', bg: 'bg-chart-1/10' },
  { id: 'Marketing', label: 'Marketing & Media', icon: Video, color: 'text-chart-2', bg: 'bg-chart-2/10' },
  { id: 'FoodTesting', label: 'Food Testing', icon: Coffee, color: 'text-chart-4', bg: 'bg-chart-4/10' },
  { id: 'Electronics', label: 'Electronics', icon: Cpu, color: 'text-chart-2', bg: 'bg-chart-2/10' },
  { id: 'Biotech', label: 'Biotech', icon: Activity, color: 'text-chart-3', bg: 'bg-chart-3/10' },
  { id: '3DPrinting', label: '3D Printing', icon: Box, color: 'text-chart-5', bg: 'bg-chart-5/10' },
  { id: 'CoWorking', label: 'Co-working', icon: Users, color: 'text-primary', bg: 'bg-primary/10' },
];

const LABS = [
  {
    id: 1, name: 'AI & Machine Learning Lab', shortName: 'AI Lab',
    incubator: 'BIADA Innovation Hub', incubatorType: 'Govt. Incubator',
    type: 'Research', location: 'Patna, Bihar', floor: '3rd Floor, Tower B',
    description: 'High-performance AI research lab with GPU clusters, JupyterHub, and dataset libraries. Ideal for ML model training, NLP research, and deep learning experiments.',
    amenities: ['50 TFLOP GPU Cluster', 'JupyterHub Environment', '10 Workstations', 'Dataset Library', 'High-speed 1Gbps Internet', '24/7 Access'],
    capacity: 10, pricePerHour: 500, rating: 4.8, reviews: 34, available: true,
    image: 'https://images.unsplash.com/photo-1518770660439-4636190af475?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=600',
    tags: ['GPU', 'AI/ML', 'Research'],
  },
  {
    id: 2, name: 'Marketing & Media Studio', shortName: 'Media Studio',
    incubator: 'T-Hub Hyderabad', incubatorType: 'Private Incubator',
    type: 'Marketing', location: 'Hyderabad, TS', floor: '2nd Floor, Main Block',
    description: 'Professional media production studio for startups. Perfect for product demo videos, content marketing, brand films, podcast recording, and pitch videos.',
    amenities: ['4K Camera Setup', 'Green Screen Studio', 'Acoustic Audio Booth', 'Davinci Resolve Suite', 'Professional Lighting', 'Teleprompter'],
    capacity: 6, pricePerHour: 800, rating: 4.7, reviews: 28, available: true,
    image: 'https://images.unsplash.com/photo-1574717024453-354056adc766?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=600',
    tags: ['Video', 'Content', 'Branding'],
  },
  {
    id: 3, name: 'Food Science Testing Lab', shortName: 'Food Lab',
    incubator: 'IIT Patna TIDES', incubatorType: 'University Incubator',
    type: 'FoodTesting', location: 'Patna, Bihar', floor: 'Ground Floor, Science Block',
    description: 'FSSAI-grade food science lab for testing nutritional content, shelf life, microbiology, and compliance for FoodTech startups and FMCG entrepreneurs.',
    amenities: ['HPLC Spectrometer', 'pH & Viscosity Analyzer', 'Microbiology Suite', 'Texture Analyzer', 'Refrigerated Storage', 'FSSAI Compliance Kit'],
    capacity: 8, pricePerHour: 1200, rating: 4.9, reviews: 19, available: false,
    image: 'https://images.unsplash.com/photo-1576086213369-97a306d36557?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=600',
    tags: ['FSSAI', 'FoodTech', 'Testing'],
  },
  {
    id: 4, name: 'Electronics & Prototyping Lab', shortName: 'Electronics Lab',
    incubator: 'BIADA Innovation Hub', incubatorType: 'Govt. Incubator',
    type: 'Electronics', location: 'Patna, Bihar', floor: '4th Floor, Innovation Block',
    description: 'Complete hardware prototyping lab for IoT, embedded systems, PCB design, and electronics manufacturing. Equipped with professional test & measurement tools.',
    amenities: ['Oscilloscopes & DMMs', 'PCB Milling Machine', 'Soldering Stations x8', 'Component Library', 'Raspberry Pi & Arduino Kits', 'IoT Dev Kits'],
    capacity: 12, pricePerHour: 600, rating: 4.6, reviews: 41, available: true,
    image: 'https://images.unsplash.com/photo-1518770660439-4636190af475?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=600',
    tags: ['IoT', 'PCB', 'Hardware'],
  },
  {
    id: 5, name: 'Biotechnology Research Suite', shortName: 'Biotech Lab',
    incubator: 'IIT Patna TIDES', incubatorType: 'University Incubator',
    type: 'Biotech', location: 'Patna, Bihar', floor: '2nd Floor, Life Sciences Block',
    description: 'Fully equipped biotech lab for startups in healthcare, pharma, agri-biotech, and diagnostics. Access to BSL-2 facilities and analytical instruments.',
    amenities: ['PCR Machines', 'Centrifuges & Incubators', 'Microscopy Suite', 'Cell Culture Facility', 'Spectrophotometer', 'Gel Electrophoresis'],
    capacity: 6, pricePerHour: 1500, rating: 4.9, reviews: 12, available: true,
    image: 'https://images.unsplash.com/photo-1582719471384-894fbb16e074?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=600',
    tags: ['HealthTech', 'Pharma', 'AgriTech'],
  },
  {
    id: 6, name: '3D Printing & Fab Studio', shortName: '3D Lab',
    incubator: 'T-Hub Hyderabad', incubatorType: 'Private Incubator',
    type: '3DPrinting', location: 'Hyderabad, TS', floor: '5th Floor, Maker Space',
    description: 'Comprehensive digital fabrication studio for rapid prototyping, product design validation, and manufacturing for hardware and consumer product startups.',
    amenities: ['FDM Printers x6', 'SLA Resin Printer', 'Laser Cutter', 'CNC Router', 'Design Workstations', 'Post-processing Area'],
    capacity: 8, pricePerHour: 700, rating: 4.7, reviews: 23, available: true,
    image: 'https://images.unsplash.com/photo-1611532736597-de2d4265fba3?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=600',
    tags: ['Hardware', 'Product Design', 'Prototyping'],
  },
  {
    id: 7, name: 'Startup Co-working Lounge', shortName: 'Co-working',
    incubator: 'BIADA Innovation Hub', incubatorType: 'Govt. Incubator',
    type: 'CoWorking', location: 'Patna, Bihar', floor: '1st Floor, Main Building',
    description: 'Premium co-working space with hot desks, private cabins, and event space. Ideal for remote teams visiting Patna, investor meetings, and team off-sites.',
    amenities: ['Hot Desks x20', 'Private Cabins x4', 'Conference Room', 'Barista Coffee Bar', 'High-speed Internet', 'Locker Storage'],
    capacity: 24, pricePerHour: 250, rating: 4.5, reviews: 67, available: true,
    image: 'https://images.unsplash.com/photo-1527192491265-7e15c55b1ed2?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=600',
    tags: ['Hot Desk', 'Team Offsite', 'Remote Work'],
  },
  {
    id: 8, name: 'Legal & Compliance Suite', shortName: 'Legal Suite',
    incubator: 'IIT Patna TIDES', incubatorType: 'University Incubator',
    type: 'CoWorking', location: 'Patna, Bihar', floor: '6th Floor, Admin Block',
    description: 'Private consultation rooms for legal, CA, and compliance work. Access to registered address service, document stamping, and notary services on-site.',
    amenities: ['Private Cabins x3', 'Document Scanner', 'Notary Services', 'CA on Demand', 'Virtual Address', 'Secure Document Vault'],
    capacity: 6, pricePerHour: 400, rating: 4.8, reviews: 15, available: true,
    image: 'https://images.unsplash.com/photo-1450101499163-c8848c66ca85?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=600',
    tags: ['Legal', 'CA', 'Compliance'],
  },
];

const MY_BOOKINGS = [
  { id: 1, lab: 'AI & Machine Learning Lab', date: 'Today, 3:00 PM', status: 'confirmed', duration: '2h', amount: '₹1,000' },
  { id: 2, lab: 'Marketing & Media Studio', date: 'Apr 28, 10:30 AM', status: 'pending', duration: '3h', amount: '₹2,400' },
];

const TIME_SLOTS = [
  { period: 'Morning', slots: ['9:00 AM', '10:30 AM', '12:00 PM'] },
  { period: 'Afternoon', slots: ['1:30 PM', '3:00 PM', '4:30 PM'] },
  { period: 'Evening', slots: ['6:00 PM', '7:30 PM'] },
];

const DURATIONS = [1, 2, 3, 4, 6, 8];

const getSlotStatus = (labId, dateIdx, periodIdx, slotIdx) => {
  const seed = (labId * 11 + dateIdx * 7 + periodIdx * 5 + slotIdx * 3) % 10;
  if (seed < 3) return 'booked';
  if (seed < 5) return 'limited';
  return 'available';
};

const getDates = () => Array.from({ length: 7 }, (_, i) => {
  const d = new Date();
  d.setDate(d.getDate() + i);
  return d;
});

const getLabTypeConfig = (type) => LAB_TYPES.find(t => t.id === type) || LAB_TYPES[1];

// ── Lab Card Component ──
const LabCard = ({ lab, onBook }) => {
  const typeConfig = getLabTypeConfig(lab.type);
  return (
    <Card className="border-border hover:shadow-elegant transition-all duration-300 overflow-hidden flex flex-col group">
      <div className="relative h-40 overflow-hidden">
        <img src={lab.image} alt={lab.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
        <div className="absolute inset-0 bg-gradient-to-t from-background/80 to-transparent" />
        <div className="absolute top-3 left-3 flex items-center gap-2">
          <span className={cn('text-xs font-medium px-2.5 py-1 rounded-full backdrop-blur-sm border', typeConfig ? `${typeConfig.bg} ${typeConfig.color}` : 'bg-muted text-muted-foreground', 'border-current/20')}>
            {lab.type.replace('3D', '3D ').replace('FoodTesting', 'Food Testing').replace('CoWorking', 'Co-working')}
          </span>
        </div>
        <div className="absolute top-3 right-3">
          <span className={cn('text-xs font-medium px-2.5 py-1 rounded-full backdrop-blur-sm', lab.available ? 'bg-success/20 text-success border border-success/30' : 'bg-destructive/20 text-destructive border border-destructive/30')}>
            {lab.available ? '● Available' : '● Fully Booked'}
          </span>
        </div>
        <div className="absolute bottom-3 left-3">
          <p className="text-xs text-primary-foreground/80 flex items-center gap-1">
            <Building2 className="w-3 h-3" />{lab.incubator}
          </p>
        </div>
      </div>

      <CardContent className="p-5 flex flex-col flex-1">
        <div className="flex items-start justify-between mb-2">
          <h3 className="font-semibold text-foreground">{lab.name}</h3>
          <div className="flex items-center gap-0.5 flex-shrink-0 ml-2">
            <Star className="w-3.5 h-3.5 fill-warning text-warning" />
            <span className="text-xs font-bold text-foreground">{lab.rating}</span>
            <span className="text-xs text-muted-foreground">({lab.reviews})</span>
          </div>
        </div>

        <div className="flex items-center gap-3 text-xs text-muted-foreground mb-3">
          <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{lab.location}</span>
          <span className="flex items-center gap-1"><Users className="w-3 h-3" />Cap. {lab.capacity}</span>
        </div>

        <div className="flex flex-wrap gap-1.5 mb-4 flex-1">
          {lab.tags.map(tag => <span key={tag} className="badge-muted">{tag}</span>)}
        </div>

        <div className="flex items-center justify-between mt-auto">
          <div>
            <span className="text-lg font-bold text-foreground">₹{lab.pricePerHour}</span>
            <span className="text-xs text-muted-foreground">/hour</span>
          </div>
          <Button
            size="sm"
            className={cn(lab.available ? 'gradient-primary text-primary-foreground border-0' : 'border-border text-muted-foreground')}
            variant={lab.available ? 'default' : 'outline'}
            disabled={!lab.available}
            onClick={() => onBook(lab)}
          >
            {lab.available ? 'Book Slot' : 'Notify Me'} <ChevronRight className="w-3.5 h-3.5 ml-1" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

// ── Booking Modal ──
const BookingModal = ({ lab, open, onClose }) => {
  const [selectedDate, setSelectedDate] = useState(0);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [duration, setDuration] = useState(2);
  const [purpose, setPurpose] = useState('');
  const [step, setStep] = useState('select'); // select | confirm | success
  const dates = getDates();

  const total = lab ? lab.pricePerHour * duration : 0;

  const handleConfirm = () => {
    if (!selectedSlot) { toast.error('Please select a time slot'); return; }
    setStep('confirm');
  };

  const handleBook = () => {
    setStep('success');
    toast.success(`Lab booked successfully! Confirmation sent to your email.`);
  };

  const reset = () => { setStep('select'); setSelectedSlot(null); setDuration(2); setPurpose(''); onClose(); };

  if (!lab) return null;

  return (
    <Dialog open={open} onOpenChange={reset}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        {step === 'success' ? (
          <div className="py-8 text-center">
            <motion.div initial={{ scale: 0.7, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
              className="w-16 h-16 rounded-2xl bg-success/15 flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-success" />
            </motion.div>
            <h3 className="text-xl font-bold text-foreground mb-2">Lab Booked! 🎉</h3>
            <p className="text-muted-foreground text-sm mb-2">
              <strong className="text-foreground">{lab.name}</strong>
            </p>
            <p className="text-sm text-muted-foreground mb-6">
              {dates[selectedDate].toLocaleDateString('en-IN', { weekday: 'long', month: 'long', day: 'numeric' })}{' '}
              at <strong className="text-foreground">{selectedSlot}</strong> for <strong className="text-foreground">{duration} hour(s)</strong>
            </p>
            <div className="p-4 rounded-xl bg-muted/50 border border-border text-sm mb-6">
              <div className="flex justify-between mb-2">
                <span className="text-muted-foreground">Booking ID</span>
                <span className="font-mono font-medium text-foreground">BK{Date.now().toString().slice(-6)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Total Paid</span>
                <span className="font-bold text-success">₹{total.toLocaleString()}</span>
              </div>
            </div>
            <Button className="gradient-primary text-primary-foreground border-0 w-full" onClick={reset}>View My Bookings</Button>
          </div>
        ) : step === 'confirm' ? (
          <>
            <DialogHeader>
              <DialogTitle>Confirm Your Booking</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-2">
              <div className="flex gap-3 p-4 rounded-xl bg-muted/50 border border-border">
                <img src={lab.image} alt={lab.name} className="w-16 h-16 rounded-xl object-cover flex-shrink-0" />
                <div>
                  <p className="font-semibold text-foreground">{lab.name}</p>
                  <p className="text-xs text-muted-foreground">{lab.incubator} • {lab.location}</p>
                </div>
              </div>
              {[['Date', dates[selectedDate].toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long' })], ['Time Slot', selectedSlot], ['Duration', `${duration} hour(s)`], ['Floor', lab.floor]].map(([k, v]) => (
                <div key={k} className="flex justify-between text-sm border-b border-border pb-2">
                  <span className="text-muted-foreground">{k}</span><span className="font-medium text-foreground">{v}</span>
                </div>
              ))}
              <div className="flex justify-between font-bold">
                <span className="text-foreground">Total Amount</span>
                <span className="text-primary text-lg">₹{total.toLocaleString()}</span>
              </div>
              <div className="p-3 rounded-xl bg-primary/8 border border-primary/20 text-xs text-muted-foreground">
                Payment via Razorpay. Free cancellation up to 24 hours before slot.
              </div>
              <div className="flex gap-3">
                <Button variant="outline" className="flex-1 border-border" onClick={() => setStep('select')}>Edit Booking</Button>
                <Button className="flex-1 gradient-primary text-primary-foreground border-0" onClick={handleBook}>Confirm & Pay ₹{total.toLocaleString()}</Button>
              </div>
            </div>
          </>
        ) : (
          <>
            <DialogHeader>
              <div className="flex items-start gap-4">
                <img src={lab.image} alt={lab.name} className="w-16 h-16 rounded-xl object-cover flex-shrink-0" />
                <div>
                  <DialogTitle className="text-base">{lab.name}</DialogTitle>
                  <p className="text-xs text-muted-foreground mt-0.5">{lab.incubator} • {lab.floor}</p>
                  <div className="flex items-center gap-2 mt-1.5">
                    <span className="badge-success">Available</span>
                    <span className="flex items-center gap-1 text-xs text-muted-foreground"><Star className="w-3 h-3 fill-warning text-warning" />{lab.rating}</span>
                    <span className="text-xs font-bold text-primary">₹{lab.pricePerHour}/hr</span>
                  </div>
                </div>
              </div>
            </DialogHeader>

            <div className="space-y-5 mt-3">
              {/* Date Picker */}
              <div>
                <Label className="text-sm font-semibold text-foreground mb-3 block">Select Date</Label>
                <div className="flex gap-2 overflow-x-auto pb-1">
                  {dates.map((d, i) => (
                    <button key={i} onClick={() => { setSelectedDate(i); setSelectedSlot(null); }}
                      className={cn(
                        'flex-shrink-0 w-14 py-2.5 rounded-xl border text-center transition-all',
                        selectedDate === i ? 'border-primary bg-primary/10 text-primary' : 'border-border hover:border-primary/40 text-muted-foreground'
                      )}>
                      <p className="text-xs">{d.toLocaleDateString('en', { weekday: 'short' })}</p>
                      <p className={cn('text-lg font-bold', selectedDate === i ? 'text-primary' : 'text-foreground')}>{d.getDate()}</p>
                      <p className="text-xs">{d.toLocaleDateString('en', { month: 'short' })}</p>
                    </button>
                  ))}
                </div>
              </div>

              {/* Time Slots */}
              <div>
                <Label className="text-sm font-semibold text-foreground mb-3 block">Select Time Slot</Label>
                <div className="space-y-3">
                  {TIME_SLOTS.map((period, pIdx) => (
                    <div key={period.period}>
                      <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2">{period.period}</p>
                      <div className="flex flex-wrap gap-2">
                        {period.slots.map((slot, sIdx) => {
                          const status = getSlotStatus(lab.id, selectedDate, pIdx, sIdx);
                          const isSelected = selectedSlot === slot;
                          return (
                            <button key={slot} disabled={status === 'booked'}
                              onClick={() => setSelectedSlot(slot)}
                              className={cn(
                                'px-4 py-2 rounded-xl text-sm font-medium border transition-all',
                                status === 'booked' ? 'border-border bg-muted/50 text-muted-foreground/50 cursor-not-allowed line-through' :
                                isSelected ? 'border-primary bg-primary/10 text-primary' :
                                status === 'limited' ? 'border-warning/50 bg-warning/8 text-warning hover:border-warning' :
                                'border-border hover:border-primary/40 text-foreground'
                              )}>
                              {slot}
                              {status === 'limited' && !isSelected && <span className="text-xs ml-1 text-warning">• 2 left</span>}
                              {isSelected && <Check className="w-3.5 h-3.5 inline ml-1.5" />}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
                <div className="flex gap-4 mt-3 text-xs text-muted-foreground">
                  <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-success"></span>Available</span>
                  <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-warning"></span>Limited</span>
                  <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-muted-foreground/40"></span>Booked</span>
                </div>
              </div>

              {/* Duration */}
              <div>
                <Label className="text-sm font-semibold text-foreground mb-3 block">Duration</Label>
                <div className="flex flex-wrap gap-2">
                  {DURATIONS.map(d => (
                    <button key={d} onClick={() => setDuration(d)}
                      className={cn('px-4 py-2 rounded-xl text-sm font-medium border transition-all',
                        duration === d ? 'border-primary bg-primary/10 text-primary' : 'border-border hover:border-primary/40 text-foreground'
                      )}>
                      {d}h <span className="text-xs text-muted-foreground">(₹{(lab.pricePerHour * d).toLocaleString()})</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Purpose */}
              <div>
                <Label>Purpose of Booking <span className="text-muted-foreground text-xs">(optional)</span></Label>
                <Textarea className="mt-1.5" rows={2} placeholder="Brief description of your work..." value={purpose} onChange={e => setPurpose(e.target.value)} />
              </div>

              {/* Amenities */}
              <div className="p-4 rounded-xl bg-muted/50 border border-border">
                <p className="text-xs font-semibold text-foreground mb-2">What's included:</p>
                <div className="flex flex-wrap gap-1.5">
                  {lab.amenities.map(a => <span key={a} className="badge-muted">{a}</span>)}
                </div>
              </div>

              {/* Summary */}
              <div className="flex items-center justify-between p-4 rounded-xl bg-primary/8 border border-primary/15">
                <div>
                  <p className="text-xs text-muted-foreground">Total Amount</p>
                  <p className="text-2xl font-bold text-foreground">₹{total.toLocaleString()}</p>
                  <p className="text-xs text-muted-foreground">{duration}h × ₹{lab.pricePerHour}/hr</p>
                </div>
                <Button className="gradient-primary text-primary-foreground border-0 h-11 px-6" onClick={handleConfirm} disabled={!selectedSlot}>
                  {selectedSlot ? 'Book Now' : 'Select a Slot'} <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default function Labs() {
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [bookingLab, setBookingLab] = useState(null);

  const filtered = useMemo(() => LABS.filter(lab => {
    const matchType = filter === 'all' || lab.type === filter;
    const matchSearch = !search || lab.name.toLowerCase().includes(search.toLowerCase()) ||
      lab.incubator.toLowerCase().includes(search.toLowerCase()) ||
      lab.tags.some(t => t.toLowerCase().includes(search.toLowerCase()));
    return matchType && matchSearch;
  }), [filter, search]);

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Lab Access</h1>
          <p className="text-muted-foreground text-sm mt-1">Book research, testing, and creative labs from PartnerNexus incubators</p>
        </div>
        <div className="flex items-center gap-2">
          <span className="badge-info">8 labs available</span>
          <span className="badge-success">3 incubators</span>
        </div>
      </div>

      <Tabs defaultValue="discover">
        <TabsList className="bg-muted">
          <TabsTrigger value="discover">Discover Labs</TabsTrigger>
          <TabsTrigger value="mybookings">My Bookings ({MY_BOOKINGS.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="discover" className="mt-6 space-y-5">
          {/* Search + Filter row */}
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input placeholder="Search labs, incubators, equipment..." className="pl-9" value={search} onChange={e => setSearch(e.target.value)} />
            </div>
          </div>

          {/* Type filter chips */}
          <div className="flex flex-wrap gap-2">
            {LAB_TYPES.map(type => (
              <button key={type.id} onClick={() => setFilter(type.id)}
                className={cn(
                  'flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-medium border transition-all',
                  filter === type.id
                    ? 'border-primary bg-primary/10 text-primary'
                    : 'border-border text-muted-foreground hover:border-primary/30 hover:text-foreground'
                )}>
                {type.icon && <type.icon className="w-3 h-3" />}
                {type.label}
              </button>
            ))}
          </div>

          {/* Stats strip */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[['8', 'Total Labs', 'text-primary'], ['3', 'Incubators', 'text-chart-2'], ['4', 'Available Now', 'text-success'], ['₹250/hr', 'Starting From', 'text-chart-4']].map(([v, l, c]) => (
              <div key={l} className="p-3 rounded-xl bg-card border border-border flex items-center gap-3">
                <span className={cn('text-xl font-bold', c)}>{v}</span>
                <span className="text-xs text-muted-foreground">{l}</span>
              </div>
            ))}
          </div>

          {/* Lab Cards Grid */}
          {filtered.length === 0 ? (
            <div className="text-center py-12">
              <Zap className="w-12 h-12 text-muted-foreground mx-auto mb-4 opacity-30" />
              <p className="text-muted-foreground">No labs found matching your search</p>
              <Button variant="outline" size="sm" className="mt-3 border-border" onClick={() => { setFilter('all'); setSearch(''); }}>Clear Filters</Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
              {filtered.map(lab => <LabCard key={lab.id} lab={lab} onBook={setBookingLab} />)}
            </div>
          )}
        </TabsContent>

        {/* My Bookings */}
        <TabsContent value="mybookings" className="mt-6">
          <div className="space-y-3">
            {MY_BOOKINGS.map(b => (
              <Card key={b.id} className="border-border">
                <CardContent className="p-5">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <Calendar className="w-5 h-5 text-primary" />
                      </div>
                      <div>
                        <p className="font-semibold text-sm text-foreground">{b.lab}</p>
                        <p className="text-xs text-muted-foreground">{b.date} • {b.duration} • {b.amount}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className={b.status === 'confirmed' ? 'badge-success' : 'badge-warning'}>{b.status}</span>
                      <Button size="sm" variant="outline" className="border-border">Details</Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>
      </Tabs>

      <BookingModal lab={bookingLab} open={!!bookingLab} onClose={() => setBookingLab(null)} />
    </div>
  );
}
